import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import RouteLoadingScreen from './RouteLoadingScreen';

export const ProtectedRoute = ({
  children,
  requireAdmin = false,
  requireStrictAdmin = false,
  requireApproved = false
}) => {
  const { user, loading, isAuthenticated, isAdmin, isStrictAdmin, isApproved } = useAuth();
  const location = useLocation();
  const isPendingStudent = user?.role === 'student' && user?.status === 'pending';
  const isSuspendedStudent = user?.role === 'student' && user?.status === 'suspended';
  const pendingAllowedPaths = ['/student/pending', '/student/chat', '/student/checkout', '/student/checkout/success', '/student/level-test'];

  // Show loading state
  if (loading) {
    return <RouteLoadingScreen />;
  }

  // Check if user is authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check admin requirement
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/student/dashboard" replace />;
  }

  if (requireStrictAdmin && !isStrictAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  if (isSuspendedStudent && location.pathname !== '/student/pending') {
    return <Navigate to="/student/pending" replace />;
  }

  if (isPendingStudent && !pendingAllowedPaths.includes(location.pathname)) {
    return <Navigate to="/student/pending" replace />;
  }

  // Check approval requirement for students
  if (requireApproved && !isApproved && !isAdmin) {
    return <Navigate to="/student/pending" replace />;
  }

  return children;
};

export const PublicOnlyRoute = ({ children }) => {
  const { loading, isAuthenticated, isAdmin, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return <RouteLoadingScreen />;
  }

  // Redirect authenticated users to their dashboard
  if (isAuthenticated) {
    const from = location.state?.from?.pathname;
    if (from) {
      return <Navigate to={from} replace />;
    }
    if (user?.role === 'student' && user?.status === 'suspended') {
      return <Navigate to="/student/pending" replace />;
    }
    if (user?.role === 'student' && user?.status === 'pending') {
      return <Navigate to="/student/pending" replace />;
    }
    return <Navigate to={isAdmin ? "/admin/dashboard" : "/student/dashboard"} replace />;
  }

  return children;
};
